/**
 * Business Intelligence page — the executive view.
 *
 * Every metric on this page is fetched from /api/business-intelligence/*.
 * No business calculations happen in this component — it only renders.
 */
import React, { useEffect, useState } from "react";
import {
  Activity, TrendingUp, TrendingDown, AlertTriangle, ShieldCheck,
  Package, Boxes, Store, Calendar, Globe2, Sparkles, ArrowUp, ArrowDown, Minus,
} from "lucide-react";
import { api } from "../lib/api";
import { formatINR as money, formatNumber as num, formatPct } from "../lib/format";
import ModuleEmptyState from "../components/app/ModuleEmptyState";
const pct = (n) => formatPct(n, { asFraction: false, digits: 1 });

const TREND_META = {
  rapid_growth:  { color: "text-emerald-400", icon: TrendingUp, label: "Rapid growth" },
  growing:       { color: "text-emerald-400", icon: ArrowUp,    label: "Growing" },
  stable:        { color: "text-zinc-400",   icon: Minus,       label: "Stable" },
  declining:     { color: "text-amber-400",  icon: ArrowDown,   label: "Declining" },
  rapid_decline: { color: "text-red-400",    icon: TrendingDown,label: "Rapid decline" },
  unknown:       { color: "text-zinc-500",   icon: Minus,       label: "—" },
};

const SEVERITY_BORDER = {
  opportunity: "border-emerald-500/40 bg-emerald-500/5",
  info:        "border-zinc-700 bg-zinc-900/30",
  warning:     "border-amber-500/40 bg-amber-500/5",
  risk:        "border-red-500/40 bg-red-500/5",
};

function KPICard({ label, value, sub, testid }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 transition-colors hover:border-zinc-700" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">{label}</p>
      <p className="mt-3 text-2xl font-semibold tracking-tight text-zinc-50 tabular-nums" style={{ fontFamily: "var(--font-heading)" }}>{value}</p>
      {sub && <p className="mt-1 text-xs text-zinc-500">{sub}</p>}
    </div>
  );
}

function BenchmarkRow({ label, payload, money: isMoney }) {
  if (!payload) return null;
  const m = TREND_META[payload.trend] || TREND_META.unknown;
  const Icon = m.icon;
  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-zinc-800/40" data-testid={`bench-row-${label}`}>
      <span className="text-sm text-zinc-300">{label}</span>
      <div className="flex items-center gap-4">
        <span className="text-sm text-zinc-100 tabular-nums">{isMoney ? money(payload.current) : num(payload.current)}</span>
        <span className={`text-xs ${m.color} flex items-center gap-1 min-w-[80px] justify-end`}>
          <Icon className="h-3 w-3" />
          {pct(payload.change_pct)}
        </span>
      </div>
    </div>
  );
}

function HealthScoreCard({ data }) {
  if (!data) return null;
  const overall = data.overall_score;
  const color = overall >= 80 ? "text-emerald-400" : overall >= 65 ? "text-emerald-300" : overall >= 45 ? "text-amber-400" : "text-red-400";
  const verdictColor = overall >= 65 ? "text-emerald-400" : overall >= 45 ? "text-amber-400" : "text-red-400";
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="bi-health-card">
      <div className="flex items-start gap-5">
        <div className="h-20 w-20 rounded-2xl bg-zinc-950 border border-zinc-800 flex items-center justify-center">
          <ShieldCheck className={`h-8 w-8 ${color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold">Business Health Score</p>
          <div className="flex items-end gap-3 mt-1">
            <p className={`text-5xl font-semibold tracking-tight ${color}`} style={{ fontFamily: "var(--font-heading)" }} data-testid="bi-health-score">
              {overall}<span className="text-zinc-600 text-2xl">/100</span>
            </p>
            <span className={`text-xs uppercase tracking-[0.16em] font-bold ${verdictColor} mb-2`} data-testid="bi-health-verdict">
              {data.verdict.replace("_", " ")}
            </span>
          </div>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-2">
            {Object.entries(data.factors).map(([k, f]) => {
              const ratio = f.score / Math.max(f.max, 1);
              const bar = ratio >= 0.7 ? "bg-emerald-500" : ratio >= 0.4 ? "bg-amber-500" : "bg-red-500";
              return (
                <div key={k} className="px-2 py-2 rounded-md border border-zinc-800/80 bg-zinc-950/50">
                  <p className="text-[10px] text-zinc-500 truncate">{f.label}</p>
                  <p className="text-sm text-zinc-100 tabular-nums">{f.score.toFixed(1)}<span className="text-zinc-600 text-[10px]">/{f.max}</span></p>
                  <div className="mt-1 h-1 rounded-full bg-zinc-800 overflow-hidden">
                    <div className={`h-1 ${bar}`} style={{ width: `${ratio * 100}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {data.suggestions.length > 0 && (
            <ul className="mt-4 space-y-1.5" data-testid="bi-health-suggestions">
              {data.suggestions.map((s, i) => (
                <li key={i} className="text-xs text-zinc-400 flex items-start gap-2">
                  <Sparkles className="h-3 w-3 text-orange-400 mt-0.5 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function InsightCard({ insight }) {
  const border = SEVERITY_BORDER[insight.severity] || SEVERITY_BORDER.info;
  const priorityColor = insight.priority === "critical" || insight.priority === "high" ? "text-red-400" : insight.priority === "medium" ? "text-amber-400" : "text-zinc-400";
  return (
    <div className={`rounded-xl border p-4 ${border}`} data-testid={`bi-insight-${insight.insight_id}`}>
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-zinc-100 font-medium">{insight.title}</p>
            <span className={`text-[10px] uppercase tracking-[0.16em] font-bold ${priorityColor}`}>{insight.priority}</span>
          </div>
          <p className="mt-1 text-xs text-zinc-400 leading-relaxed">{insight.description}</p>
          <p className="mt-2 text-xs text-zinc-300"><span className="text-zinc-500">Recommended action:</span> {insight.recommended_action}</p>
        </div>
      </div>
    </div>
  );
}

function ProductRow({ row }) {
  const m = TREND_META[row.trend] || TREND_META.unknown;
  const Icon = m.icon;
  return (
    <tr className="border-t border-zinc-800/60 hover:bg-zinc-800/20" data-testid={`bi-product-${row.product_id}`}>
      <td className="px-3 py-2 text-sm text-zinc-100 max-w-[280px] truncate">{row.name}</td>
      <td className="px-3 py-2 text-sm text-zinc-400 tabular-nums hidden md:table-cell">{row.sku || "—"}</td>
      <td className="px-3 py-2 text-sm text-zinc-100 tabular-nums">{money(row.total_revenue)}</td>
      <td className="px-3 py-2 text-sm text-zinc-300 tabular-nums hidden sm:table-cell">{num(row.total_units)}</td>
      <td className="px-3 py-2 text-sm text-zinc-300 tabular-nums hidden md:table-cell">{row.contribution_pct.toFixed(1)}%</td>
      <td className={`px-3 py-2 text-xs ${m.color}`}><Icon className="inline h-3 w-3" /> {pct(row.revenue_change_pct)}</td>
      <td className="px-3 py-2 text-xs">
        <span className={`px-2 py-0.5 rounded-md font-medium ${
          row.health_bucket === "excellent" ? "bg-emerald-500/15 text-emerald-300" :
          row.health_bucket === "healthy"   ? "bg-emerald-500/10 text-emerald-300/80" :
          row.health_bucket === "needs_attention" ? "bg-amber-500/15 text-amber-300" :
                                              "bg-red-500/15 text-red-300"
        }`}>{row.health_score}</span>
      </td>
    </tr>
  );
}

export default function BusinessIntelligence() {
  const [overview, setOverview] = useState(null);
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState(null);
  const [marketplaces, setMarketplaces] = useState(null);
  const [trends, setTrends] = useState(null);
  const [insights, setInsights] = useState(null);
  const [geo, setGeo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [o, p, c, m, t, i, g] = await Promise.all([
          api.get("/business-intelligence/overview"),
          api.get("/business-intelligence/products"),
          api.get("/business-intelligence/categories"),
          api.get("/business-intelligence/marketplaces"),
          api.get("/business-intelligence/trends?grain=month&days_back=180"),
          api.get("/business-intelligence/insights"),
          api.get("/business-intelligence/geography"),
        ]);
        setOverview(o.data); setProducts(p.data); setCategories(c.data);
        setMarketplaces(m.data); setTrends(t.data); setInsights(i.data); setGeo(g.data);
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading || !overview) {
    return <div className="text-sm text-zinc-500" data-testid="bi-loading">Analyzing your business…</div>;
  }
  const kpis = overview.kpis;
  const bench = overview.benchmarks;
  const rates = overview.rate_changes;
  const periods = overview.revenue_by_period;

  // Empty state — no imports yet on this org.
  if (!kpis || (kpis.orders || 0) === 0) {
    return (
      <div className="max-w-7xl mx-auto" data-testid="bi-page">
        <ModuleEmptyState
          icon={Sparkles}
          title="Upload order history to generate AI insights"
          description="Business Intelligence turns your imported orders into KPIs, health scores, insights and benchmarks. Import your first CSV to bring this page to life."
          testid="bi-empty"
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-12" data-testid="bi-page">
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-2">Business Intelligence</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-50" style={{ fontFamily: "var(--font-heading)" }}>
          Executive overview
        </h1>
        <p className="mt-2 text-zinc-400 max-w-2xl">
          Every chart, KPI and insight on this page is computed by the Milkath BI Engine — your single source of truth.
        </p>
      </header>

      {/* Executive KPIs */}
      <section>
        <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4">Executive KPIs</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KPICard label="Gross revenue"      value={money(kpis.gross_revenue)} testid="bi-kpi-gross-revenue" />
          <KPICard label="Net revenue"        value={money(kpis.net_revenue)}   testid="bi-kpi-net-revenue" sub={`${money(kpis.discount_total)} discount`} />
          <KPICard label="Orders"             value={num(kpis.orders)}          testid="bi-kpi-orders" />
          <KPICard label="Units sold"         value={num(kpis.units_sold)}      testid="bi-kpi-units" />
          <KPICard label="AOV"                value={money(kpis.aov)}           testid="bi-kpi-aov" />
          <KPICard label="ASP"                value={money(kpis.asp)}           testid="bi-kpi-asp" />
          <KPICard label="Units / order"      value={kpis.avg_units_per_order.toFixed(2)} testid="bi-kpi-units-per-order" />
          <KPICard label="Unique products"    value={num(kpis.unique_products)} testid="bi-kpi-unique-products" />
          <KPICard label="Categories"         value={num(kpis.unique_categories)} testid="bi-kpi-categories" />
          <KPICard label="Marketplaces"       value={num(kpis.unique_marketplaces)} testid="bi-kpi-marketplaces" />
          <KPICard label="Tax collected"      value={money(kpis.tax_total)}     testid="bi-kpi-tax" />
          <KPICard label="Shipping collected" value={money(kpis.shipping_total)} testid="bi-kpi-shipping" />
        </div>
      </section>

      {/* Health */}
      <HealthScoreCard data={overview.health} />

      {/* Growth vs periods */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="bi-benchmarks">
          <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-3">Revenue benchmarks</h3>
          <BenchmarkRow label="Yesterday vs day before" payload={bench.yesterday?.revenue} money />
          <BenchmarkRow label="This week vs last week" payload={bench.last_week?.revenue} money />
          <BenchmarkRow label="This month vs last month" payload={bench.last_month?.revenue} money />
          <BenchmarkRow label="This quarter vs last quarter" payload={bench.last_quarter?.revenue} money />
          <BenchmarkRow label="This year vs last year" payload={bench.last_year?.revenue} money />
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="bi-rates">
          <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-3">Rate movements</h3>
          <BenchmarkRow label="Return rate (MoM)" payload={rates?.return_rate} />
          <BenchmarkRow label="Cancellation rate (MoM)" payload={rates?.cancellation_rate} />
          <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
            <PeriodCard label="Today" value={periods.today?.revenue} />
            <PeriodCard label="This week" value={periods.week?.revenue} />
            <PeriodCard label="This month" value={periods.month?.revenue} />
            <PeriodCard label="This quarter" value={periods.quarter?.revenue} />
            <PeriodCard label="This year" value={periods.year?.revenue} />
            <PeriodCard label="Yesterday" value={periods.yesterday?.revenue} />
          </div>
        </div>
      </section>

      {/* Insights */}
      {insights && insights.length > 0 && (
        <section data-testid="bi-insights-section">
          <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4">Business insights</h2>
          <div className="grid lg:grid-cols-2 gap-3">
            {insights.slice(0, 8).map((ins) => <InsightCard key={ins.insight_id} insight={ins} />)}
          </div>
        </section>
      )}

      {/* Product intelligence */}
      <section data-testid="bi-products-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2"><Package className="h-3.5 w-3.5" /> Product intelligence</h2>
          <span className="text-xs text-zinc-500">{products?.total_products || 0} products tracked</span>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-zinc-800 bg-zinc-900/40">
          <table className="w-full">
            <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left font-bold">Product</th>
                <th className="px-3 py-2 text-left font-bold hidden md:table-cell">SKU</th>
                <th className="px-3 py-2 text-left font-bold">Revenue</th>
                <th className="px-3 py-2 text-left font-bold hidden sm:table-cell">Units</th>
                <th className="px-3 py-2 text-left font-bold hidden md:table-cell">Contribution</th>
                <th className="px-3 py-2 text-left font-bold">Trend</th>
                <th className="px-3 py-2 text-left font-bold">Health</th>
              </tr>
            </thead>
            <tbody>
              {(products?.rows || []).slice(0, 15).map((row) => <ProductRow key={row.product_id} row={row} />)}
            </tbody>
          </table>
        </div>
      </section>

      {/* Categories + Marketplaces */}
      <section className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="bi-categories">
          <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-3 flex items-center gap-2"><Boxes className="h-3.5 w-3.5" /> Categories</h3>
          {(categories?.rows || []).slice(0, 8).map((r) => (
            <div key={r.category} className="flex justify-between items-center py-2 px-2 rounded hover:bg-zinc-800/30">
              <span className="text-sm text-zinc-200 truncate flex-1">{r.category}</span>
              <span className="text-sm text-zinc-300 tabular-nums">{money(r.revenue)}</span>
              <span className="text-xs text-zinc-500 min-w-[60px] text-right">{r.contribution_pct.toFixed(1)}%</span>
              <span className={`text-xs ml-3 min-w-[60px] text-right ${(TREND_META[r.trend] || TREND_META.unknown).color}`}>{pct(r.revenue_change_pct)}</span>
            </div>
          ))}
          {(!categories?.rows || categories.rows.length === 0) && <p className="text-sm text-zinc-500">No categorized products yet.</p>}
        </div>
        <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5" data-testid="bi-marketplaces">
          <h3 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-3 flex items-center gap-2"><Store className="h-3.5 w-3.5" /> Marketplaces</h3>
          {(marketplaces?.rows || []).slice(0, 8).map((r) => (
            <div key={r.marketplace} className="flex justify-between items-center py-2 px-2 rounded hover:bg-zinc-800/30">
              <span className="text-sm text-zinc-200 truncate flex-1">{r.marketplace}</span>
              <span className="text-sm text-zinc-300 tabular-nums">{money(r.revenue)}</span>
              <span className="text-xs text-zinc-500 min-w-[60px] text-right">{r.contribution_pct.toFixed(1)}%</span>
              <span className={`text-xs ml-3 min-w-[60px] text-right ${(TREND_META[r.trend] || TREND_META.unknown).color}`}>{pct(r.revenue_change_pct)}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Time intelligence */}
      {trends?.day_of_week && (
        <section data-testid="bi-time-section">
          <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4 flex items-center gap-2"><Calendar className="h-3.5 w-3.5" /> Time intelligence</h2>
          <div className="grid grid-cols-7 gap-2">
            {trends.day_of_week.map((d) => {
              const max = Math.max(...trends.day_of_week.map((x) => x.revenue || 0), 1);
              const h = Math.max(4, Math.round(((d.revenue || 0) / max) * 80));
              return (
                <div key={d.dow} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 text-center" data-testid={`bi-dow-${d.dow}`}>
                  <p className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">{d.label.slice(0, 3)}</p>
                  <div className="mt-2 mx-auto w-2 bg-orange-500/40 rounded-sm" style={{ height: `${h}px` }} />
                  <p className="mt-2 text-xs text-zinc-300 tabular-nums">{money(d.revenue)}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Geography (capability-gated) */}
      {geo?.available && (
        <section data-testid="bi-geo-section">
          <h2 className="text-sm font-bold uppercase tracking-[0.16em] text-zinc-500 mb-4 flex items-center gap-2"><Globe2 className="h-3.5 w-3.5" /> Geography</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
              <p className="text-xs text-zinc-500 mb-3">By state</p>
              {geo.by_state.slice(0, 8).map((s) => (
                <div key={s.state} className="flex justify-between items-center py-1.5">
                  <span className="text-sm text-zinc-200">{s.state}</span>
                  <span className="text-sm text-zinc-300 tabular-nums">{money(s.revenue)}</span>
                </div>
              ))}
            </div>
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
              <p className="text-xs text-zinc-500 mb-3">Top cities</p>
              {geo.by_city.slice(0, 8).map((c) => (
                <div key={c.city} className="flex justify-between items-center py-1.5">
                  <span className="text-sm text-zinc-200">{c.city}</span>
                  <span className="text-sm text-zinc-300 tabular-nums">{money(c.revenue)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function PeriodCard({ label, value }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950/40 p-2">
      <p className="text-[10px] text-zinc-500 uppercase tracking-[0.14em]">{label}</p>
      <p className="text-sm text-zinc-100 tabular-nums">{money(value)}</p>
    </div>
  );
}
